import { useEffect, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useHistory, useParams } from "react-router";
import { Box, CircularProgress, Typography, Button, TextField, InputAdornment } from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import { useGetContentModelItemsQuery, useGetContentModelsQuery } from "../../../shell/services/instance";
import emptyItemsList from "../../../../public/images/emptyItemsList.png";
import { CreateVariantDialog } from "../components/CreateVariantDialog";
import { fetchModels } from "../../../shell/store/models";
import { useDispatch } from "react-redux";
import { fetchFields } from "../../../shell/store/fields";
import { useParams as useSearchParams } from "../../../shell/hooks/useParams";
import { ContentItem } from "shell/services/types";
import SearchBox from "shell/components/SearchBox";
import blockPlaceholder from "../../../../public/images/blockPlaceholder.png";
export const BlockModel = () => {
  const {
    t
  } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();
  const {
    modelZUID
  } = useParams<{
    modelZUID: string;
  }>();
  const [params] = useSearchParams();
  const [showCreateVariantDialog, setShowCreateVariantDialog] = useState(false);
  const [search, setSearch] = useState("");
  const {
    data,
    isLoading,
    error,
    isUninitialized
  } = useGetContentModelItemsQuery({
    modelZUID
  });
  const {
    data: models
  } = useGetContentModelsQuery();

  // Used to ensure that the models and fields are fetched into legacy redux store in order for create variant to function correctly
  useEffect(() => {
    dispatch(fetchModels());
    dispatch(fetchFields(modelZUID));
  }, []);
  useEffect(() => {
    setShowCreateVariantDialog(!!params?.get("createVariant"));
  }, [params]);
  const filteredData = useMemo(() => {
    if (!data) return [];
    const normalizedSearch = search.toLowerCase().trim();
    if (!normalizedSearch) return data;
    return data?.filter?.(item => item.web.metaTitle?.toLowerCase().includes(normalizedSearch));
  }, [data, search]);
  const model = models?.find(model => model.ZUID === modelZUID);
  if (isLoading || isUninitialized) {
    return <Box display="flex" justifyContent="center" alignItems="center" height="100%" width="100%">
        <CircularProgress />
      </Box>;
  }
  return <>
      <Box width="100%" display="flex" flexDirection="column" sx={{
      backgroundColor: "grey.50"
    }} data-sentry-element="Box" data-sentry-source-file="BlockModel.tsx">
        <Box display="flex" justifyContent="space-between" alignItems="center" px={4} pt={4} pb={1.75} sx={{
        borderBottom: theme => `2px solid ${theme.palette.border}`,
        backgroundColor: "background.paper"
      }} data-sentry-element="Box" data-sentry-source-file="BlockModel.tsx">
          <Typography variant="h3" fontWeight="700" data-sentry-element="Typography" data-sentry-source-file="BlockModel.tsx">
            {model?.label}
          </Typography>
          <Box display={"flex"} alignItems="center" gap={2} data-sentry-element="Box" data-sentry-source-file="BlockModel.tsx">
            <SearchBox data-cy="search-blocks-input" value={search} onChange={event => setSearch(event.target.value)} size="small" sx={{
            width: "240px",
            "& .MuiOutlinedInput-notchedOutline": {
              border: 0
            }
          }} InputProps={{
            sx: {
              backgroundColor: "grey.50"
            },
            startAdornment: <InputAdornment position="start">
                    <SearchRoundedIcon fontSize="small" color="action" />
                  </InputAdornment>
          }} placeholder={t("shell.blockSelectorSearchVariants")} data-sentry-element="SearchBox" data-sentry-source-file="BlockModel.tsx" />
            <Button variant="contained" size="small" startIcon={<AddRoundedIcon />} onClick={() => setShowCreateVariantDialog(true)} data-cy="create-variant-button-header" data-sentry-element="Button" data-sentry-source-file="BlockModel.tsx">
              {t("shell.blockSelectorCreateVariant")}
            </Button>
          </Box>
        </Box>
        {data?.length ? <Box px={4.5} py={4} display="flex" gap={2} flexWrap={"wrap"} sx={{
        overflowY: "scroll"
      }}>
            {filteredData.map(item => <BlockVariantCard key={item.meta.ZUID} item={item} onClick={() => {
          history.push(`/blocks/${modelZUID}/${item.meta.ZUID}`);
        }} />)}
          </Box> : <Box display="flex" height="100%" alignItems="center" justifyContent="center" px={4} gap={6}>
            <Box>
              <Typography variant="h4" fontWeight={700}>
                {t("blocks.startCreatingVariantsNow")}
              </Typography>
              <Typography variant="body1" color="text.secondary" mt={1} mb={2}>
                {t("blocks.addVariantsEmptyStateBody")}
              </Typography>
              <Button variant="contained" size="small" startIcon={<AddRoundedIcon />} onClick={() => setShowCreateVariantDialog(true)} data-cy="create-variant-button">
                {t("shell.blockSelectorCreateVariant")}
              </Button>
            </Box>
            <Box>
              <img height={420} width={582} src={emptyItemsList} alt={t("blocks.emptyItemsListAlt")} />
            </Box>
          </Box>}
      </Box>
      {showCreateVariantDialog && <CreateVariantDialog onClose={() => setShowCreateVariantDialog(false)} model={model} />}
    </>;
};
type BlockVariantCardProps = {
  item: ContentItem;
  onClick: () => void;
};
const BlockVariantCard = ({
  item,
  onClick
}: BlockVariantCardProps) => {
  const [noImage, setNoImage] = useState(false);
  return <Box data-cy="block-variant-card" key={item.meta.ZUID} width={265} sx={{
    border: theme => `1px solid ${theme.palette.border}`,
    boxSizing: "border-box",
    cursor: "pointer"
  }} onClick={onClick} data-sentry-element="Box" data-sentry-component="BlockVariantCard" data-sentry-source-file="BlockModel.tsx">
      <Box px={1} pt={1} pb={1.75} data-sentry-element="Box" data-sentry-source-file="BlockModel.tsx">
        {!noImage ? <Box component="img" src={item.data.og_image as string} minHeight={146} maxHeight={146} width="100%" sx={{
        objectFit: "contain"
      }} onError={(e: any) => {
        setNoImage(true);
      }} /> : <Box minHeight={146} maxHeight={146} width="100%" component="img" sx={{
        objectFit: "contain"
      }} src={blockPlaceholder} />}
      </Box>
      <Box py={2} px={1} sx={{
      backgroundColor: "background.paper"
    }} height={52} data-sentry-element="Box" data-sentry-source-file="BlockModel.tsx">
        <Typography noWrap variant="body2" data-sentry-element="Typography" data-sentry-source-file="BlockModel.tsx">
          {item.web.metaTitle ?? ""}
        </Typography>
      </Box>
    </Box>;
};